import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    Divider,
    LinearProgress,
    Chip
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Schedule as ScheduleIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../utils/responsiveMedia';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';
import IconButton from '../../common/salonPublicAppearance/SalonPublicIconButton';

/**
 * ServiceSelectionStep - Étape 2 du flux de réservation
 * 
 * Permet de sélectionner les prestations.
 * - Mode Simple (isConsecutive=false) : Sélection unique
 * - Mode Consécutif (isConsecutive=true) : Sélection multiple
 */
const ServiceSelectionStep = ({
    salonId,
    services = [],
    categories = [],
    isConsecutive = false,
    initialSelection = [],
    onContinue,
    onBack,
    onCancel,
    currentStep = 2,
    totalSteps = 4
}) => {
    const { t } = useTranslation();
    const { tokens: theme } = useSalonPublicTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedServices, setSelectedServices] = useState(initialSelection);

    // Filtrer les services selon la recherche
    const filteredServices = useMemo(() => {
        if (!searchTerm) return services;
        const lowerTerm = searchTerm.toLowerCase();
        return services.filter(service =>
            service.name.toLowerCase().includes(lowerTerm) // || service.category_name?.toLowerCase().includes(lowerTerm)
        );
    }, [services, searchTerm]);

    // Grouper les services par catégorie
    const servicesByCategory = useMemo(() => {
        const grouped = {};
        // Si categorie fournie
        filteredServices.forEach(service => {
            const catName = service.category_name || t('client_services.others');
            if (!grouped[catName]) grouped[catName] = [];
            grouped[catName].push(service);
        });
        return grouped;
    }, [filteredServices]);

    // Calcul du total
    const total = useMemo(() => {
        return selectedServices.reduce((acc, curr) => {
            return {
                price: acc.price + Number(curr.price || 0),
                duration: acc.duration + Number(curr.duration || 0)
            };
        }, { price: 0, duration: 0 });
    }, [selectedServices]);

    const handleServiceToggle = (service) => {
        if (isConsecutive) {
            // Mode Multiple
            const exists = selectedServices.find(s => s.service_id === service.service_id);
            if (exists) {
                setSelectedServices(prev => prev.filter(s => s.service_id !== service.service_id));
            } else {
                setSelectedServices(prev => [...prev, service]);
            }
        } else {
            // Mode Simple : on remplace tout par le nouveau
            setSelectedServices([service]);
        }
    };

    const handleRemove = (serviceId) => {
        setSelectedServices(prev => prev.filter(s => s.service_id !== serviceId));
    };

    const isSelected = (serviceId) => {
        return selectedServices.some(s => s.service_id === serviceId);
    };

    const canClientBook = (service) => {
        const isClientBookingDisabled =
            service?.client_booking_disabled === true ||
            service?.client_booking_disabled === 'true';
        return !!service?.has_assigned_employees && !isClientBookingDisabled;
    };

    return (
        <Container
            data-testid="service-selection-theme-root"
            maxWidth="xl"
            disableGutters
            style={{ fontFamily: theme.typography.fontFamily.sans.join(', ') }}
            sx={{
            py: { xs: 2, md: 0 },
            px: { xs: 2, md: 0 },
            width: '100%',
            maxWidth: '100%',
            margin: 0,
            color: theme.salon.onBackground,
            [HIGH_DENSITY_TABLET_MEDIA]: {
                px: 4,
            },
        }}>
            {/* Bouton Retour */}
            <Box sx={{ 
                mb: 3, 
                px: { xs: 0, md: 0 },
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
                maxWidth: 1200,
                mx: 'auto',
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    maxWidth: 1440,
                },
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{
                        color: theme.salon.onBackground,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: theme.salon.surfaceMuted,
                        }
                    }}
                >
                    {t('client_services.back')}
                </Button>
            </Box>

            <Grid container spacing={4} sx={{
                width: '100%',
                maxWidth: 1200,
                mx: 'auto',
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(340px, 420px)',
                    gap: 4,
                    alignItems: 'start',
                    maxWidth: 1500,
                    '& > .MuiGrid-root': {
                        width: '100%',
                        maxWidth: 'none',
                        flexBasis: 'auto',
                    },
                }
            }}>
                {/* Colonne Gauche : Liste des services */}
                <Grid item xs={12} md={8} sx={{ px: { xs: 1, md: 0 }, [HIGH_DENSITY_TABLET_MEDIA]: { gridColumn: '1' } }}>
                    {/* Barre de recherche */}
                    <TextField
                        fullWidth
                        placeholder={t('client_services.search_service_placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            mb: 4,
                            backgroundColor: theme.salon.surfaceMuted,
                            borderRadius: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                color: theme.salon.onBackground,
                                '& fieldset': { borderColor: theme.salon.border },
                                '&:hover fieldset': { borderColor: theme.salon.primary },
                            },
                            '& input::placeholder': {
                                color: theme.salon.onBackground,
                                opacity: 0.72,
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme.salon.primary }} />
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Liste des catégories */}
                    {Object.entries(servicesByCategory).map(([category, catServices]) => (
                        <Box key={category} sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', fontWeight: 600, color: theme.salon.onBackground }}>
                                {/* Icone catégorie placeholders si besoin */}
                                {category}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {catServices.map(service => {
                                    const active = isSelected(service.service_id);
                                    const canBook = canClientBook(service);
                                    return (
                                        <Card
                                            key={service.service_id}
                                            data-testid={`booking-service-card-${service.service_id}`}
                                            elevation={0}
                                            sx={{
                                                border: active ? `2px solid ${theme.salon.primary}` : `1px solid ${theme.salon.border}`,
                                                borderRadius: 3,
                                                backgroundColor: theme.salon.compatibilityMode ? '#ffffff' : theme.salon.surfaceMuted,
                                                color: theme.salon.compatibilityMode ? theme.colors.gray[900] : theme.salon.onBackground,
                                                transition: 'all 0.2s',
                                                cursor: canBook ? 'pointer' : 'default',
                                                '&:hover': {
                                                    borderColor: canBook
                                                        ? theme.salon.primary
                                                        : theme.salon.border
                                                }
                                            }}
                                            onClick={() => {
                                                if (!canBook) return;
                                                handleServiceToggle(service);
                                            }}
                                        >
                                            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '24px !important', [HIGH_DENSITY_TABLET_MEDIA]: { p: '32px !important' } }}>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.salon.compatibilityMode ? theme.colors.gray[900] : theme.salon.onBackground, mb: 0.5, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.18rem' } }}>
                                                        {service.name}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 2, color: theme.salon.compatibilityMode ? theme.colors.gray[600] : theme.salon.onBackground }}>
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1rem' } }}>
                                                            <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, color: theme.salon.primary }} />
                                                            {service.duration} min
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.salon.primary, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1rem' } }}>
                                                            {service.price} DA
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {canBook ? (
                                                <IconButton
                                                    aria-label={active ? t('client_services.selected') : t('client_services.select')}
                                                    sx={{
                                                        backgroundColor: active ? theme.salon.primary : theme.salon.background,
                                                        border: active ? 'none' : `1px solid ${theme.salon.primary}`,
                                                        color: active ? (theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary) : theme.salon.primary,
                                                        '&:hover': { backgroundColor: active ? theme.salon.primary : theme.salon.surface }
                                                    }}
                                                >
                                                    {active ? <CheckIcon /> : <AddIcon />}
                                                </IconButton>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: theme.colors.warning[900], backgroundColor: theme.colors.warning[50], borderRadius: 1.5, px: 1.25, py: 0.75, fontStyle: 'italic', textAlign: 'right', maxWidth: 220 }}>
                                                        {t('client_services.contact_salon')}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}

                    {Object.keys(servicesByCategory).length === 0 && (
                        <Typography sx={{ textAlign: 'center', color: theme.colors.gray[500], mt: 4 }}>
                            Aucune prestation trouvée pour "{searchTerm}"
                        </Typography>
                    )}
                </Grid>

                {/* Colonne Droite : Ma Sélection - Sticky */}
                <Grid item xs={12} md={4} sx={{ px: { xs: 1, md: 0 }, [HIGH_DENSITY_TABLET_MEDIA]: { gridColumn: '2' } }}>
                    <Box sx={{ position: 'sticky', top: 24, [HIGH_DENSITY_TABLET_MEDIA]: { top: 32 } }}>
                        <Paper data-testid="booking-selection-summary" elevation={0} sx={{ p: 3, borderRadius: 4, border: `1px solid ${theme.salon.border}`, backgroundColor: theme.salon.compatibilityMode ? '#ffffff' : theme.salon.surfaceMuted, color: theme.salon.compatibilityMode ? theme.colors.gray[900] : theme.salon.onBackground, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', [HIGH_DENSITY_TABLET_MEDIA]: { p: 4 } }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: 'inherit' }}>{t('client_services.my_selection')}</Typography>
                            <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.72, mb: 3, display: 'block' }}>
                                {isConsecutive ? t('client_services.drag_reorder_soon') : t('client_services.one_service_selected')}
                            </Typography>

                            {selectedServices.length === 0 ? (
                                <Box sx={{ py: 4, textAlign: 'center', border: `1px dashed ${theme.colors.gray[300]}`, borderRadius: 2, mb: 3 }}>
                                    <Typography variant="body2" sx={{ color: theme.colors.gray[400] }}>
                                        {t('client_services.empty_cart')}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                                    {selectedServices.map((service, index) => (
                                        <Box
                                            key={`${service.service_id}-${index}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                                backgroundColor: theme.salon.background,
                                                color: theme.salon.onBackground,
                                                borderRadius: 2
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {/* Icon drag si consecutive ? */}
                                                {isConsecutive && <DragIndicatorIcon sx={{ color: theme.colors.gray[300], fontSize: 20, cursor: 'grab' }} />}
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}>{service.name}</Typography>
                                                    <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.72 }}>
                                                        {service.duration} min • {service.price} DA
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <IconButton
                                                aria-label={t('client_services.remove')}
                                                size="small"
                                                onClick={() => handleRemove(service.service_id)}
                                                sx={{ color: theme.colors.gray[400] }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Divider sx={{ mb: 3, borderColor: theme.salon.border }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.72 }}>{t('client_services.total_duration')}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>{total.duration} {t('client_services.min')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'inherit' }}>{t('client_services.total')}</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.salon.primary }}>{total.price} DA</Typography>
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={selectedServices.length === 0}
                                onClick={() => onContinue(selectedServices)}
                                sx={{
                                    fontWeight: 700,
                                    '&.Mui-disabled': { backgroundColor: theme.colors.gray[200], color: theme.colors.gray[400] }
                                }}
                            >
                                {t('client_services.continue')}
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'inherit', opacity: 0.72, fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                                {t('client_services.payment_on_site')}
                            </Typography>
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ServiceSelectionStep;
